import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { treeActions } from '../../redux/slices/treeSlice';
import { findAutomaticMappings, getVisibleMappings, getKeysToExpandForMappedNodes, validateMapping, findNodeByKey } from '../../utils/autoMapping';
import { useToast } from '../../hooks/useToast';
import performanceMonitor from '../../utils/performanceMonitor';
import './MappingConnections.css';

// Performance optimization utilities
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const throttle = (func, limit) => {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

const SimpleMappingConnections = ({ saveMappingToRedux, middleCurrTab, setMiddleCurrTab }) => {
  const dispatch = useDispatch();
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [mouseHasMoved, setMouseHasMoved] = useState(false);
  const [isAutoMappingLoading, setIsAutoMappingLoading] = useState(false);
  const { toast, showError, hideToast } = useToast();

  const isMappingMode = useSelector(state => state.treeData.mappingStates.isMappingMode);
  const isLinkMode = useSelector(state => state.treeData.mappingStates.isLinkMode);
  const mappings = useSelector(state => state.treeData.mappingStates.mappings || []);
  const automaticMappings = useSelector(state => state.treeData.mappingStates.automaticMappings || []);
  const showOnlySelectedMappings = useSelector(state => state.treeData.mappingStates.showOnlySelectedMappings);
  const autoMatchingEnabled = useSelector(state => state.treeData.mappingStates.autoMatchingEnabled);
  const currentTab = useSelector(state => state.treeData.mappingStates.currentMappingTab);
  const rightTreeCurrentTab = useSelector(state => state.treeData.outbound.rightTree.currentTab);

  // Get tree data for auto-matching
  const outboundTree = useSelector(state => state.treeData.outbound);
  const inboundDependencyTree = useSelector(state => state.treeData.outbound.leftTree.inboundDependencyTree);

  // Get all selected source nodes (we'll show mappings for all of them)
  const allSelectedSourceNodes = useSelector(state => state.treeData.mappingStates.selectedSourceNodes);

  // Get all selected target nodes from the right tree (multiple selections)
  const allSelectedTargetNodes = useSelector(state => state.treeData.mappingStates.selectedTargetNodes);
  const hiddenMappingLines = useSelector(state => state.treeData.mappingStates.hiddenMappingLines || { source: false, target: false });

  // For backward compatibility, still provide a single selectedSourceNode
  // This will be used by existing logic, but we'll enhance the mapping visibility to consider all selections
  const selectedSourceNode = allSelectedSourceNodes.header?.[0] || allSelectedSourceNodes.body?.[0] || allSelectedSourceNodes.trailer?.[0] || null;

  // Get currently selected target nodes for ALL tabs (not just current tab)
  // This ensures we show mappings for selected nodes in any tab
  const allTabSelectedTargetNodes = [];
  Object.entries(allSelectedTargetNodes).forEach(([tabName, nodeArray]) => {
    if (Array.isArray(nodeArray)) {
      nodeArray.forEach(node => {
        if (node && node.key) {
          allTabSelectedTargetNodes.push(node);
        }
      });
    }
  });

  // For backward compatibility with single target selection (keeping the old selector for now)
  const selectedTargetNode = useSelector(state => {
    const tabMapping = ['header', 'body', 'trailer'];
    for (const tabName of tabMapping) {
      const node = state.treeData.outbound?.rightTree?.tree?.[tabName]?.selectedNode || null;
      if (node && node.key) return node; // Make sure we have a valid node with a key
    }
    return null;
  });

  // State to trigger re-render when tree nodes expand/collapse
  const [treeChangeCounter, setTreeChangeCounter] = useState(0);

  // Debounced tree change handler
  const debouncedTreeChange = useMemo(
    () => debounce(() => {
      setTreeChangeCounter(prev => prev + 1);
    }, 100),
    []
  );

  // Monitor auto-mapping loading state using custom events
  useEffect(() => {
    const handleAutoMappingStart = () => {
      setIsAutoMappingLoading(true);
    };

    const handleAutoMappingFinish = () => {
      setIsAutoMappingLoading(false);
    };

    // Listen for auto-mapping events
    window.addEventListener('autoMappingStarted', handleAutoMappingStart);
    window.addEventListener('autoMappingFinished', handleAutoMappingFinish);

    return () => {
      window.removeEventListener('autoMappingStarted', handleAutoMappingStart);
      window.removeEventListener('autoMappingFinished', handleAutoMappingFinish);
    };
  }, []);

  // Monitor tree expansion/collapse changes with optimized observer
  useEffect(() => {
    try {
      if (!isMappingMode) {
        return;
      }

      const sourceContainer = document.getElementById('mappingTreeContainer');
      const targetContainer = document.getElementById('rightTreeOutbound');

      // Also monitor specific tab containers within the target tree
      const targetTabContainers = [
        document.getElementById('outboundTreeHeader'),
        document.getElementById('outboundTreeBody'),
        document.getElementById('outboundTreeTrailer')
      ].filter(Boolean);

      const allContainers = [sourceContainer, targetContainer, ...targetTabContainers].filter(Boolean);


      if (allContainers.length === 0) {
        return;
      }
      let observer;
      let timeoutId;

      const handleMutation = (mutations) => {
        let hasTreeChanges = false;

        for (const mutation of mutations) {
          // Check if the mutation involves tree node expansion/collapse
          if (mutation.type === 'childList' ||
            (mutation.type === 'attributes' &&
              (mutation.attributeName === 'class' || mutation.attributeName === 'style'))) {
            hasTreeChanges = true;
            break; // Early exit for better performance
          }
        }

        if (hasTreeChanges) {
          // Clear existing timeout and set new one
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            debouncedTreeChange();
          }, 50);
        }
      };

              observer = new MutationObserver(handleMutation);

              // Observe all containers (source, target, and target tab containers)
      allContainers.forEach(container => {
        if (container) {
          observer.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style']
          });
        }
      });

      return () => {
        if (observer) observer.disconnect();
        if (timeoutId) clearTimeout(timeoutId);
      };
    } catch (error) {
      console.error('Error in main useEffect:', error);
    }
  }, [isMappingMode, currentTab]);

  // Improved visibility check that handles cross-tab and collapsed nodes better
  const isNodeVisible = useCallback((key, side, mappingType = null) => {
    try {
      let containers = [];

      if (side === 'source') {
        // For right-to-right mappings, source is also in the right tree
        if (mappingType === 'right-to-right') {
          containers = [
            document.getElementById('rightTreeOutbound'),
            document.getElementById('outboundTreeHeader'),
            document.getElementById('outboundTreeBody'),
            document.getElementById('outboundTreeTrailer')
          ].filter(Boolean);
        } else {
          containers = [
            document.getElementById('mappingTreeHeader'),
            document.getElementById('mappingTreeBody'),
            document.getElementById('mappingTreeTrailer')
          ].filter(Boolean);
        }
      } else {
        // For target side, check multiple possible containers
        containers = [
          document.getElementById('rightTreeOutbound'),
          document.getElementById('outboundTreeHeader'),
          document.getElementById('outboundTreeBody'),
          document.getElementById('outboundTreeTrailer')
        ].filter(Boolean);
      }

      if (containers.length === 0) {
        return true; // Default to visible if no containers found
      }

      // Check each container for the node
      for (let container of containers) {
        const keyElement = container.querySelector(`[data-key="${key}"]`);

        if (keyElement) {
          const treeNode = keyElement.closest('.rc-tree-treenode');
          if (treeNode) {
            // Check if the node exists and has valid dimensions
            const rect = treeNode.getBoundingClientRect();
            const style = window.getComputedStyle(treeNode);
            
            // More lenient visibility check - only check if element exists and has dimensions
            // Don't check if it's outside viewport, as we want to show lines for out-of-viewport nodes
            const hasValidDimensions = rect.height > 0 && rect.width > 0;
            const isNotHidden = style.display !== 'none' && style.visibility !== 'hidden';
            const isVisible = hasValidDimensions && isNotHidden;

            // Additional check for tab visibility - ensure node is in the currently active tab
            const tabPanel = treeNode.closest('[role="tabpanel"]');
            if (tabPanel) {
              const tabId = tabPanel.getAttribute('id') || '';
              const isActiveTab = tabId.includes('header') && currentTab === 0 ||
                tabId.includes('body') && currentTab === 1 ||
                tabId.includes('trailer') && currentTab === 2;

              // For cross-tab mappings, be more lenient about tab visibility
              // Only hide target nodes if they're not in the active tab AND we're not in mapping mode
              if (!isActiveTab && side === 'target' && !isMappingMode) {
                return false;
              }
            }

            // Return true if the node exists and has valid dimensions, regardless of viewport position
            // This allows lines to be drawn for nodes that are outside the viewport
            return isVisible;
          }
        }
      }

      // If we can't find the node in any container, it might be collapsed or in a different tab
      // Be more lenient and return true to allow lines to be drawn
      return true;
    } catch (error) {
      return true; // Default to visible on error
    }
  }, [currentTab, isMappingMode]); // Include isMappingMode dependency

  // Get current tab name and selected nodes (used in multiple places)
  const tabNames = ['header', 'body', 'trailer'];
  const currentTabName = tabNames[currentTab];
  const currentTabSourceNodes = allSelectedSourceNodes[currentTabName] || [];
  const currentTabTargetNodes = allSelectedTargetNodes[currentTabName] || [];

      // Memoized visible mappings calculation for better performance
    const visibleMappings = useMemo(() => {


    let filteredMappings = [];

    if (showOnlySelectedMappings) {
      // Collect all potential source nodes that should show mappings
      const potentialSourceNodes = new Set();

      // Add selected source nodes from current tab
              currentTabSourceNodes.forEach(node => {
          if (node && node.key) {
            potentialSourceNodes.add(node.key);
          }
        });

      // Add selected target nodes from current tab
              currentTabTargetNodes.forEach(node => {
          if (node && node.key) {
            potentialSourceNodes.add(node.key);
          }
        });

      // Add selected target node from current tab (backwards compatibility)
      if (selectedTargetNode && selectedTargetNode.key) {
        // Only add if the selected target node is from the current tab
        const tabMapping = ['header', 'body', 'trailer'];
        const selectedTargetTab = tabMapping[currentTab];
        const currentTabSelectedNode = outboundTree?.rightTree?.tree?.[selectedTargetTab]?.selectedNode;

        if (currentTabSelectedNode && currentTabSelectedNode.key === selectedTargetNode.key) {
          potentialSourceNodes.add(selectedTargetNode.key);
        }
      }

            // Only add nodes that are actually selected - be very restrictive
      // This ensures we only show mappings for specifically selected nodes
      const allMappings = [...mappings, ...automaticMappings];
      allMappings.forEach(mapping => {
        // Get current tab positions
        const leftTreeTab = currentTab; // Left tree current tab
        const rightTreeTab = rightTreeCurrentTab || 1; // Right tree current tab (default to body)
        
        // Convert rightTreeTab to number if it's a string
        const rightTreeTabNum = typeof rightTreeTab === 'string' ? 
          (rightTreeTab === 'header' ? 0 : rightTreeTab === 'body' ? 1 : rightTreeTab === 'trailer' ? 2 : 1) : 
          rightTreeTab;
        
        // Check if this is a right-to-right mapping
        const isRightToRightMapping = mapping.mappingType === 'right-to-right';
        
        // Check if this mapping is relevant to current tab positions
        const isSameTabMapping = mapping.sourceTab === mapping.targetTab && mapping.sourceTab === leftTreeTab;
        const isCrossTabMapping = mapping.sourceTab === leftTreeTab && mapping.targetTab === rightTreeTabNum;
        const isRightToRightInCurrentContext = isRightToRightMapping && 
          (mapping.sourceTab === rightTreeTabNum || mapping.targetTab === rightTreeTabNum);
        
        // Only process mappings that are relevant to current tab positions
        if (isSameTabMapping || isCrossTabMapping || isRightToRightInCurrentContext) {
          let sourceSelected = false;
          let targetSelected = false;
          
          if (isRightToRightMapping) {
            // For right-to-right mappings, both source and target are in the right tree
            // Check if source node is selected in the right tree (target nodes)
            sourceSelected = currentTabTargetNodes.some(node => node.key === mapping.sourceKey);
            // Check if target node is selected in the right tree (target nodes)
            targetSelected = currentTabTargetNodes.some(node => node.key === mapping.targetKey);
          } else {
            // For left-to-right mappings, source is in left tree, target is in right tree
            sourceSelected = currentTabSourceNodes.some(node => node.key === mapping.sourceKey);
            targetSelected = currentTabTargetNodes.some(node => node.key === mapping.targetKey);
          }
          
          // Only add if the source OR target is specifically selected (not both)
          if (sourceSelected || targetSelected) {
            // Only add the selected node, not both
            if (sourceSelected) {
              potentialSourceNodes.add(mapping.sourceKey);
            }
            if (targetSelected) {
              potentialSourceNodes.add(mapping.targetKey);
            }
          }
        }
      });





      // Show all mappings that are relevant to current tab positions
      // The final filtering will handle selection-based visibility
      filteredMappings = [...mappings, ...automaticMappings].filter(mapping => {
        // Get current tab positions
        const leftTreeTab = currentTab; // Left tree current tab
        const rightTreeTab = rightTreeCurrentTab || 1; // Right tree current tab (default to body)
        
        // Convert rightTreeTab to number if it's a string
        const rightTreeTabNum = typeof rightTreeTab === 'string' ? 
          (rightTreeTab === 'header' ? 0 : rightTreeTab === 'body' ? 1 : rightTreeTab === 'trailer' ? 2 : 1) : 
          rightTreeTab;
        
        // Show mapping if:
        // 1. Same-tab mapping: both source and target are in the same tab, OR
        // 2. Cross-tab mapping: either source or target is in the current tab context, OR
        // 3. Right-to-right mapping: both source and target are in the right tree (target side)
        const isSameTabMapping = mapping.sourceTab === mapping.targetTab && mapping.sourceTab === leftTreeTab;
        const isCrossTabMapping = mapping.sourceTab === leftTreeTab && mapping.targetTab === rightTreeTabNum;
        
        // For cross-tab mappings, be more lenient - show if either source or target is in current context
        const isSourceInCurrentContext = mapping.sourceTab === leftTreeTab;
        const isTargetInCurrentContext = mapping.targetTab === rightTreeTabNum;
        const isCrossTabInCurrentContext = isSourceInCurrentContext || isTargetInCurrentContext;
        
        // Check if this is a right-to-right mapping
        const isRightToRightMapping = mapping.mappingType === 'right-to-right';
        
        // For right-to-right mappings, check if either source or target is in the current right tree tab
        const isRightToRightInCurrentContext = isRightToRightMapping && 
          (mapping.sourceTab === rightTreeTabNum || mapping.targetTab === rightTreeTabNum);
        
        const shouldInclude = isSameTabMapping || isCrossTabMapping || isCrossTabInCurrentContext || isRightToRightInCurrentContext;
        

        
        return shouldInclude;
      });
    } else {
      // Show all mappings when not filtering by selection
      filteredMappings = getVisibleMappings(
        mappings,
        automaticMappings,
        selectedSourceNode,
        selectedTargetNode,
        showOnlySelectedMappings
      );
    }



        // Filter out mappings where source or target nodes are not visible (collapsed)
    // Also filter by current tab to only show relevant mappings
    const finalMappings = filteredMappings.filter(mapping => {
      // Check if this is a right-to-right mapping (declare first to avoid ReferenceError)
      const isRightToRightMapping = mapping.mappingType === 'right-to-right';
      
      const sourceVisible = isNodeVisible(mapping.sourceKey, 'source', mapping.mappingType);
      const targetVisible = isNodeVisible(mapping.targetKey, 'target', mapping.mappingType);
      
      // Check if either source or target is selected in their respective tabs
      const tabNames = ['header', 'body', 'trailer'];
      
      // Check source node selection in source tab
      const sourceTabName = tabNames[mapping.sourceTab];
      const sourceTabSourceNodes = allSelectedSourceNodes[sourceTabName] || [];
      const sourceTabTargetNodes = allSelectedTargetNodes[sourceTabName] || [];
      const sourceSelected = sourceTabSourceNodes.some(node => node.key === mapping.sourceKey) ||
                            sourceTabTargetNodes.some(node => node.key === mapping.sourceKey);
      
      // Check target node selection in target tab
      const targetTabName = tabNames[mapping.targetTab];
      const targetTabSourceNodes = allSelectedSourceNodes[targetTabName] || [];
      const targetTabTargetNodes = allSelectedTargetNodes[targetTabName] || [];
      const targetSelected = targetTabSourceNodes.some(node => node.key === mapping.targetKey) ||
                            targetTabTargetNodes.some(node => node.key === mapping.targetKey);
      
      // For cross-tab mappings and right-to-right mappings, be more lenient with visibility
      // Only require the selected node to be visible, not both
      const isCrossTabMapping = mapping.sourceTab !== mapping.targetTab;
      
      let shouldShow;
      if (isCrossTabMapping || isRightToRightMapping) {
        // For cross-tab mappings and right-to-right mappings: if source is selected, only source needs to be visible
        // If target is selected, only target needs to be visible
        if (sourceSelected) {
          shouldShow = sourceVisible;
        } else if (targetSelected) {
          shouldShow = targetVisible;
        } else {
          shouldShow = false;
        }
      } else {
        // For same-tab mappings: be more lenient - if either node is selected and visible, show the mapping
        // This allows lines to be drawn even if one node is outside viewport
        shouldShow = (sourceSelected || targetSelected) && (sourceVisible || targetVisible);
      }
      
      return shouldShow;
    });





    return finalMappings;
  }, [
    showOnlySelectedMappings,
    mappings,
    automaticMappings,
    allSelectedSourceNodes,
    allSelectedTargetNodes,
    allTabSelectedTargetNodes,
    selectedTargetNode,
    selectedSourceNode,
    isNodeVisible,
    currentTab,
    rightTreeCurrentTab
  ]);

  // Auto-match fields when tree data changes
  // NOTE: Disabled to prevent infinite loops - cross-tab auto-mapping is handled in Outbound.jsx
  /*
  useEffect(() => {
    if (!autoMatchingEnabled || !isMappingMode) return;
    
    const tabMapping = ['header', 'body', 'trailer'];
    const currentTabName = tabMapping[currentTab];
    const sourceTreeData = Array.isArray(inboundDependencyTree?.[currentTabName]) 
      ? inboundDependencyTree[currentTabName] 
      : [];
    const targetTreeData = outboundTree?.rightTree?.tree?.[currentTabName]?.translatedData || [];
    
    if (sourceTreeData.length > 0 && targetTreeData.length > 0) {
      const newCurrentTabMappings = findAutomaticMappings(
        sourceTreeData, 
        targetTreeData, 
        currentTab
      );
      
      // Only update if we have new mappings for the current tab
      if (newCurrentTabMappings.length > 0) {
        // Preserve cross-tab mappings and replace only current tab mappings
        const existingCrossTabMappings = automaticMappings.filter(mapping => 
          mapping.sourceTab !== currentTab || mapping.targetTab !== currentTab
        );
        const mergedMappings = [...existingCrossTabMappings, ...newCurrentTabMappings];
        
        // Only dispatch if the merged mappings are different from current
        const mappingsChanged = mergedMappings.length !== automaticMappings.length ||
          !mergedMappings.every(mapping => 
            automaticMappings.some(existing => 
              existing.sourceKey === mapping.sourceKey && 
              existing.targetKey === mapping.targetKey &&
              existing.sourceTab === mapping.sourceTab &&
              existing.targetTab === mapping.targetTab
            )
          );
        
        if (mappingsChanged) {
          dispatch(treeActions.setAutomaticMappings(mergedMappings));
        }
      }
    }
  }, [inboundDependencyTree, outboundTree?.rightTree?.tree, currentTab, autoMatchingEnabled, isMappingMode, outboundTree?.leftTree?.body?.selectedNode?.key, dispatch]);
  */

  // Auto-match when selected node changes
  // NOTE: Disabled to prevent infinite loops - cross-tab auto-mapping is handled in Outbound.jsx
  /*
  useEffect(() => {
    if (!autoMatchingEnabled || !isMappingMode || (!selectedSourceNode && !selectedTargetNode)) return;
    
    const tabMapping = ['header', 'body', 'trailer'];
    const currentTabName = tabMapping[currentTab];
    const sourceTreeData = Array.isArray(inboundDependencyTree?.[currentTabName]) 
      ? inboundDependencyTree[currentTabName] 
      : [];
    const targetTreeData = outboundTree?.rightTree?.tree?.[currentTabName]?.translatedData || [];
    
    if (sourceTreeData.length > 0 && targetTreeData.length > 0) {
      const newCurrentTabMappings = findAutomaticMappings(
        sourceTreeData, 
        targetTreeData, 
        currentTab
      );
      
      // Only update if we have new mappings for the current tab
      if (newCurrentTabMappings.length > 0) {
        // Preserve cross-tab mappings and replace only current tab mappings
        const existingCrossTabMappings = automaticMappings.filter(mapping => 
          mapping.sourceTab !== currentTab || mapping.targetTab !== currentTab
        );
        const mergedMappings = [...existingCrossTabMappings, ...newCurrentTabMappings];
        
        // Only dispatch if the merged mappings are different from current
        const mappingsChanged = mergedMappings.length !== automaticMappings.length ||
          !mergedMappings.every(mapping => 
            automaticMappings.some(existing => 
              existing.sourceKey === mapping.sourceKey && 
              existing.targetKey === mapping.targetKey &&
              existing.sourceTab === mapping.sourceTab &&
              existing.targetTab === mapping.targetTab
            )
          );
        
        if (mappingsChanged) {
          dispatch(treeActions.setAutomaticMappings(mergedMappings));
        }
      }
    }
  }, [selectedSourceNode?.key, selectedTargetNode?.key, autoMatchingEnabled, isMappingMode, dispatch]);
  */

  // Auto-expand trees to reveal mapped nodes when a node is selected
  useEffect(() => {
    if (!isMappingMode) return;

    const tabMapping = ['header', 'body', 'trailer'];
    const currentTabName = tabMapping[currentTab];
    const combinedMappings = [...mappings, ...automaticMappings];

    // Check all selected source nodes from all tabs
    const allSelectedSourceKeys = [];
    Object.values(allSelectedSourceNodes).forEach(nodeArray => {
      if (Array.isArray(nodeArray)) {
        nodeArray.forEach(node => {
          if (node && node.key) {
            allSelectedSourceKeys.push(node.key);
          }
        });
      }
    });

    // Check all selected target nodes from ALL tabs for expanding source tree
    const allSelectedTargetKeys = [];
    allTabSelectedTargetNodes.forEach(node => {
      if (node && node.key && !allSelectedTargetKeys.includes(node.key)) {
        allSelectedTargetKeys.push(node.key);
      }
    });

    // If source nodes are selected, expand target tree to reveal mapped nodes
    if (allSelectedSourceKeys.length > 0) {
      const targetTreeData = outboundTree?.rightTree?.tree?.[currentTabName]?.translatedData || [];

      const allKeysToExpand = [];
      allSelectedSourceKeys.forEach(sourceKey => {
        const keysToExpand = getKeysToExpandForMappedNodes(
          combinedMappings,
          sourceKey,
          targetTreeData,
          'source-to-target'
        );
        allKeysToExpand.push(...keysToExpand);
      });

      if (allKeysToExpand.length > 0) {
        dispatch(treeActions.requestTreeExpansion({
          tree: 'rightTree',
          tab: currentTabName,
          keysToExpand: [...new Set(allKeysToExpand)] // Remove duplicates
        }));
      }
    }

  }, [
    JSON.stringify(allSelectedSourceNodes.header?.map(n => n?.key).sort() || []),
    JSON.stringify(allSelectedSourceNodes.body?.map(n => n?.key).sort() || []),
    JSON.stringify(allSelectedSourceNodes.trailer?.map(n => n?.key).sort() || []),
    JSON.stringify(allTabSelectedTargetNodes.map(n => n?.key).sort() || []), // Track all target selections across tabs
    selectedTargetNode?.key,
    mappings.length,
    automaticMappings.length,
    currentTab,
    isMappingMode,
    dispatch
  ]);

  // Create a bezier curve path between two points
  const createBezierPath = (x1, y1, x2, y2) => {
    const cp1x = x1 + (x2 - x1) / 3;
    const cp1y = y1;
    const cp2x = x2 - (x2 - x1) / 3;
    const cp2y = y2;

    return `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
  };

  // Helper function to clamp coordinates to viewport only when needed
  const clampToViewport = (x, y, width, height, tabHeight = 0, forceClamp = false) => {
    const padding = 10; // Add padding to avoid edge clipping
    const topPadding = Math.max(padding, tabHeight + 10); // Extra space below tabs

    // Only clamp if explicitly requested or if coordinates are extremely far outside viewport horizontally
    // Never clamp Y coordinates for mapping lines to allow them to show direction for nodes above/below viewport
    if (forceClamp || Math.abs(x) > width * 2) {
      return {
        x: Math.max(padding, Math.min(width - padding, x)),
        y: y // Always keep original Y position to show line direction
      };
    }

    // For normal mapping lines, only clamp X coordinates to prevent horizontal overflow
    // Allow Y coordinates to follow actual node positions (both above and below viewport)
    return {
      x: Math.max(padding, Math.min(width - padding, x)),
      y: y // Keep original Y position to follow nodes (can be above or below viewport)
    };
  };

  // Helper function to clean up dragging state and line
  const cleanupDragging = useCallback(() => {
    if (svgRef.current) {
      const draggingLine = svgRef.current.querySelector('#dragging-line');
      if (draggingLine) {
        draggingLine.remove();
      }
      // Clear any pending scroll redraws
      if (svgRef.current._scrollTimeout) {
        clearTimeout(svgRef.current._scrollTimeout);
        delete svgRef.current._scrollTimeout;
      }
    }
    setDragging(null);
    // Reset mousePos to prevent lingering state
    setMousePos({ x: 0, y: 0 });
    setMouseHasMoved(false);
  }, []);





  // Function to draw only the dragging line (for performance)
  const drawDraggingLine = useCallback((mouseX, mouseY) => {
    if (!svgRef.current || !dragging) return;

    const svg = svgRef.current;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tabHeight = 50; // Approximate tab height

    // Get the dragging line element or create it
    let draggingLine = svg.querySelector('#dragging-line');
    if (!draggingLine) {
      draggingLine = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      draggingLine.setAttribute('id', 'dragging-line');
      draggingLine.setAttribute('stroke', '#ff5722');
      draggingLine.setAttribute('stroke-width', '3');
      draggingLine.setAttribute('stroke-dasharray', '8,4');
      draggingLine.setAttribute('fill', 'none');
      draggingLine.setAttribute('opacity', '0.8');
      svg.appendChild(draggingLine);
    }

    // Calculate start and end points
    const startX = dragging.startX;
    const startY = dragging.startY;
    
    // Ensure we have valid coordinates
    if (startX === undefined || startY === undefined || isNaN(startX) || isNaN(startY)) {
      return;
    }
    
    // Use provided mouse coordinates or fall back to start position for initial draw
    let endX = mouseX !== undefined && !isNaN(mouseX) ? mouseX : startX;
    const endY = mouseY !== undefined && !isNaN(mouseY) ? mouseY : startY;

    // Ensure the line always extends to the right - never to the left
    if (endX < startX) {
      endX = startX + 50; // Minimum distance to the right
    }

    // Create bezier curve path
    const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
    
    // Don't draw very short lines that might appear as random directions
    if (distance < 5) {
      draggingLine.setAttribute('d', '');
      return;
    }
    
    const isNearlyHorizontal = Math.abs(endY - startY) < 10;
    let d;

    if (isNearlyHorizontal) {
      const midX = (startX + endX) / 2;
      const cp1x = startX + (midX - startX) * 0.3;
      const cp2x = endX - (endX - midX) * 0.3;
      d = `M ${startX} ${startY} C ${cp1x} ${startY}, ${cp2x} ${endY}, ${endX} ${endY}`;
    } else {
      d = createBezierPath(startX, startY, endX, endY);
    }

    draggingLine.setAttribute('d', d);
  }, [dragging, createBezierPath]);

  // Memoized viewport dimensions and tab height
  const viewportData = useMemo(() => {
    const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

    // Get tab section height to avoid overlap
    const tabElements = document.querySelectorAll('.MuiTabs-root');
    let tabHeight = 0;
    tabElements.forEach(tabElement => {
      const rect = tabElement.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < 100) { // Only consider tabs near the top
        tabHeight = Math.max(tabHeight, rect.bottom);
      }
    });

    return { viewportWidth, viewportHeight, tabHeight };
  }, []);

  // Memoized SVG defs creation
  const svgDefs = useMemo(() => {
    const { viewportWidth, viewportHeight, tabHeight } = viewportData;

    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

    // Create a clip path to hide lines in the tab/header area
    const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
    clipPath.setAttribute('id', 'contentClip');

    // Rectangle for the visible content area - start below tabs to avoid overlapping with UI controls
    // Extend beyond viewport height to allow lines to show direction for nodes below viewport
    const contentRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    contentRect.setAttribute('x', '0');
    contentRect.setAttribute('y', tabHeight); // Start clipping exactly at tabs
    contentRect.setAttribute('width', viewportWidth);
    contentRect.setAttribute('height', viewportHeight * 3); // Extend further beyond viewport to show lines below

    clipPath.appendChild(contentRect);
    defs.appendChild(clipPath);

    // Gradient for lines
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', 'lineGradient');
    gradient.setAttribute('x1', '0%');
    gradient.setAttribute('y1', '0%');
    gradient.setAttribute('x2', '100%');
    gradient.setAttribute('y2', '0%');

    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('style', 'stop-color:#2196f3;stop-opacity:0.8');

    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('style', 'stop-color:#1976d2;stop-opacity:1');

    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    defs.appendChild(gradient);

    // Filter for glow effect
    const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    filter.setAttribute('id', 'glow');

    const blur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
    blur.setAttribute('stdDeviation', '3');
    blur.setAttribute('result', 'coloredBlur');

    const merge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
    const mergeNode1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    mergeNode1.setAttribute('in', 'coloredBlur');
    const mergeNode2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    mergeNode2.setAttribute('in', 'SourceGraphic');

    merge.appendChild(mergeNode1);
    merge.appendChild(mergeNode2);
    filter.appendChild(blur);
    filter.appendChild(merge);
    defs.appendChild(filter);

    return defs;
  }, [viewportData]);

  // Throttled redraw connections function
  const redrawConnections = useCallback(() => {
    if (!svgRef.current) return;

    // Monitor performance
    performanceMonitor.takeMemorySnapshot('redraw-connections');

    const svg = svgRef.current;
    const { viewportWidth, viewportHeight, tabHeight } = viewportData;

    // Ensure SVG is properly sized before drawing
    if (viewportWidth <= 0 || viewportHeight <= 0) {
      return;
    }

    // Clear SVG content but preserve dragging line if it exists
    const draggingLine = svg.querySelector('#dragging-line');
    const defs = svg.querySelector('defs');
    
    // Remove only mapping connection groups, preserve defs and dragging line
    const existingConnections = svg.querySelectorAll('.mapping-connection');
    existingConnections.forEach(connection => connection.remove());
    
    // Ensure defs and dragging line are present
    if (!defs) {
      svg.appendChild(svgDefs.cloneNode(true));
    }
    if (draggingLine && dragging && !svg.contains(draggingLine)) {
      svg.appendChild(draggingLine);
    }

    // Set SVG size - extend height to accommodate lines that go below viewport
    svg.setAttribute('width', viewportWidth);
    svg.setAttribute('height', viewportHeight * 3); // Triple height to show lines below viewport
    svg.style.pointerEvents = 'none';

    // Add defs
    svg.appendChild(svgDefs.cloneNode(true));

    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();

    // Optimized mapping drawing with batching
    const batchSize = 10; // Process mappings in batches for better performance
    let processedCount = 0;

    const processMappingBatch = () => {
      const startIndex = processedCount;
      const endIndex = Math.min(startIndex + batchSize, visibleMappings.length);

              for (let i = startIndex; i < endIndex; i++) {
          const mapping = visibleMappings[i];
          
          // Check if this mapping connection already exists to prevent duplicates
          const existingConnection = svg.querySelector(`[data-mapping-id="${mapping.sourceKey}-${mapping.targetKey}-${mapping.sourceTab}-${mapping.targetTab}"]`);
          if (existingConnection) {
            continue; // Skip if already drawn
          }

          // Check if mapping lines should be hidden based on side-specific hiding state
          const isRightToRight = mapping.mappingType === 'right-to-right';
          
          // Debug logging for right-to-right mappings
          
          // For right-to-right mappings: both source and target are in the right tree (target nodes)
          // For left-to-right mappings: source is in left tree (source nodes), target is in right tree (target nodes)
          let shouldHideMapping = false;
          
          if (isRightToRight) {
            // For right-to-right mappings: hide if target side hide button is active and either node is selected
            const sourceSelected = currentTabTargetNodes.some(node => node.key === mapping.sourceKey);
            const targetSelected = currentTabTargetNodes.some(node => node.key === mapping.targetKey);
            
            // Hide if target side is hidden AND either source or target node is selected
            shouldHideMapping = hiddenMappingLines.target && (sourceSelected || targetSelected);
          } else {
            // For left-to-right mappings: check appropriate node collections
            const sourceHidden = hiddenMappingLines.source && currentTabSourceNodes.some(node => node.key === mapping.sourceKey);
            const targetHidden = hiddenMappingLines.target && currentTabTargetNodes.some(node => node.key === mapping.targetKey);
            shouldHideMapping = sourceHidden || targetHidden;
          }
          
          // Skip rendering if mapping should be hidden
          if (shouldHideMapping) {
            continue;
          }
          
        const tabMapping = ['Header', 'Body', 'Trailer'];
        const sourceKey = mapping.sourceKey;
        const targetKey = mapping.targetKey;

        let sourceContainer, targetContainer;

        if (isRightToRight) {
          // For right-to-right mappings, both source and target are in the right tree
          // Use the specific tab containers for better accuracy
          const sourceTabName = tabMapping[mapping.sourceTab] || 'Body';
          const targetTabName = tabMapping[mapping.targetTab] || 'Body';
          sourceContainer = document.getElementById(`outboundTree${sourceTabName}`);
          targetContainer = document.getElementById(`outboundTree${targetTabName}`);
          
          // Fallback to the main right tree container if specific tab containers are not found
          if (!sourceContainer) sourceContainer = document.getElementById('rightTreeOutbound');
          if (!targetContainer) targetContainer = document.getElementById('rightTreeOutbound');
        } else {
          const tabName = tabMapping[mapping.sourceTab] || 'Body';
          sourceContainer = document.getElementById(`mappingTree${tabName}`);
          targetContainer = document.getElementById('rightTreeOutbound');
        }

        if (!sourceContainer || !targetContainer) continue;

        const sourceNode = sourceContainer.querySelector(`[data-key="${sourceKey}"]`);
        const targetNode = targetContainer.querySelector(`[data-key="${targetKey}"]`);

        if (sourceNode && targetNode) {
          const sourceTreeNode = sourceNode.closest('.rc-tree-treenode');
          const targetTreeNode = targetNode.closest('.rc-tree-treenode');

          if (!sourceTreeNode || !targetTreeNode) continue;

          const sourceTitle = sourceTreeNode.querySelector('.rc-tree-title') || sourceTreeNode;
          const targetTitle = targetTreeNode.querySelector('.rc-tree-title') || targetTreeNode;

          const sourceTextSpan = sourceTitle.querySelector('span[data-key]') || sourceTitle;
          const targetTextSpan = targetTitle.querySelector('span[data-key]') || targetTitle;

          const sourceRect = sourceTextSpan.getBoundingClientRect();
          const targetRect = targetTextSpan.getBoundingClientRect();

          const sourceNodeRect = sourceTreeNode.getBoundingClientRect();
          const targetNodeRect = targetTreeNode.getBoundingClientRect();

          // Validate that we have valid coordinates (not 0,0 or default positions)
          // Allow negative top values for nodes that are above the viewport
          const isValidSourceRect = sourceRect.width > 0 && sourceRect.height > 0;
          const isValidTargetRect = targetRect.width > 0 && targetRect.height > 0;
          const isValidSourceNodeRect = sourceNodeRect.width > 0 && sourceNodeRect.height > 0;
          const isValidTargetNodeRect = targetNodeRect.width > 0 && targetNodeRect.height > 0;

          // Skip drawing if coordinates are not valid yet
          if (!isValidSourceRect || !isValidTargetRect || !isValidSourceNodeRect || !isValidTargetNodeRect) {
            continue;
          }

          // Calculate connection points
          let startX, startY, endX, endY;

          // Get SVG position to transform coordinates from viewport to SVG coordinate system
          const svgRect = svg.getBoundingClientRect();

          if (isRightToRight) {
            startX = sourceRect.left - svgRect.left - 5;
            startY = sourceNodeRect.top - svgRect.top + sourceNodeRect.height / 2;
            endX = targetRect.left - svgRect.left - 5;
            endY = targetNodeRect.top - svgRect.top + targetNodeRect.height / 2;
          } else {
            startX = sourceRect.right - svgRect.left + 5;
            startY = sourceNodeRect.top - svgRect.top + sourceNodeRect.height / 2;
            endX = targetRect.left - svgRect.left - 5;
            endY = targetNodeRect.top - svgRect.top + targetNodeRect.height / 2;
          }

          // Check if either node is outside viewport or not visible
          // Allow nodes to be above or below the viewport but below tabs area
          const tabsAreaBottom = tabHeight; // Same as clip path
          const sourceOutsideViewport = startX < 0 || startX > viewportWidth || startY > viewportHeight;
          const targetOutsideViewport = endX < 0 || endX > viewportWidth || endY > viewportHeight;
          const hasNodeOutsideViewport = sourceOutsideViewport || targetOutsideViewport;
          
          // Special check for nodes above the tabs area (but still in viewport)
          const sourceAboveTabs = startY < tabsAreaBottom;
          const targetAboveTabs = endY < tabsAreaBottom;
          const hasNodeAboveTabs = sourceAboveTabs || targetAboveTabs;
          
          // Special check for nodes below the viewport (similar to above tabs)
          const sourceBelowViewport = startY > viewportHeight;
          const targetBelowViewport = endY > viewportHeight;
          const hasNodeBelowViewport = sourceBelowViewport || targetBelowViewport;
          


          // Store original positions for better line drawing
          const originalStartX = startX;
          const originalStartY = startY;
          const originalEndX = endX;
          const originalEndY = endY;

          // If source is outside viewport, adjust the line to point towards the source's actual Y position
          if (sourceOutsideViewport) {
            // Keep the actual Y position of the source node, but clamp X to viewport edge
            startX = startX < 0 ? 10 : (startX > viewportWidth ? viewportWidth - 10 : startX);
            // Don't change startY - keep the actual Y position to show the line follows the node
          }

          // If target is outside viewport, adjust the line to point towards the target's actual Y position
          if (targetOutsideViewport) {
            // Keep the actual Y position of the target node, but clamp X to viewport edge
            endX = endX < 0 ? 10 : (endX > viewportWidth ? viewportWidth - 10 : endX);
            // Don't change endY - keep the actual Y position to show the line follows the node
          }

          // Don't clamp Y coordinates - let lines follow the actual node positions
          // The clip path will handle preventing overlap with tabs
          // For nodes below viewport, extend the line to show direction (similar to above viewport)

          const clampedStart = clampToViewport(startX, startY, viewportWidth, viewportHeight, tabHeight, false);
          const clampedEnd = clampToViewport(endX, endY, viewportWidth, viewportHeight, tabHeight, false);
          


          // Create group for the connection
          const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          g.setAttribute('class', 'mapping-connection');
          g.setAttribute('clip-path', 'url(#contentClip)');
          g.setAttribute('data-mapping-id', `${mapping.sourceKey}-${mapping.targetKey}-${mapping.sourceTab}-${mapping.targetTab}`);
          g.style.pointerEvents = 'none';

          // Create bezier path
          let d;
          if (isRightToRight) {
            const leftOffset = 30;
            const leftX = Math.min(clampedStart.x, clampedEnd.x) - leftOffset;
            d = `M ${clampedStart.x} ${clampedStart.y} L ${leftX} ${clampedStart.y} L ${leftX} ${clampedEnd.y} L ${clampedEnd.x} ${clampedEnd.y}`;
          } else {
            // Handle cases where source or target is outside viewport
            if (sourceOutsideViewport || targetOutsideViewport) {
              // Use the same bezier curve logic for out-of-viewport nodes
              const isNearlyHorizontal = Math.abs(clampedEnd.y - clampedStart.y) < 10;
              if (isNearlyHorizontal) {
                const midX = (clampedStart.x + clampedEnd.x) / 2;
                const cp1x = clampedStart.x + (midX - clampedStart.x) * 0.3;
                const cp2x = clampedEnd.x - (clampedEnd.x - midX) * 0.3;
                d = `M ${clampedStart.x} ${clampedStart.y} C ${cp1x} ${clampedStart.y}, ${cp2x} ${clampedEnd.y}, ${clampedEnd.x} ${clampedEnd.y}`;
              } else {
                d = createBezierPath(clampedStart.x, clampedStart.y, clampedEnd.x, clampedEnd.y);
              }
            } else {
              // Both nodes are visible - use normal bezier curve
              const isNearlyHorizontal = Math.abs(clampedEnd.y - clampedStart.y) < 10;
              if (isNearlyHorizontal) {
                const midX = (clampedStart.x + clampedEnd.x) / 2;
                const cp1x = clampedStart.x + (midX - clampedStart.x) * 0.3;
                const cp2x = clampedEnd.x - (clampedEnd.x - midX) * 0.3;
                d = `M ${clampedStart.x} ${clampedStart.y} C ${cp1x} ${clampedStart.y}, ${cp2x} ${clampedEnd.y}, ${clampedEnd.x} ${clampedEnd.y}`;
              } else {
                d = createBezierPath(clampedStart.x, clampedStart.y, clampedEnd.x, clampedEnd.y);
              }
            }
          }

          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', d);
          path.setAttribute('fill', 'none');

          const isNearlyHorizontal = Math.abs(clampedEnd.y - clampedStart.y) < 10;

          // Style the path - let CSS handle all styling
          if (isLinkMode) {
            path.setAttribute('stroke', '#4caf50');
            path.setAttribute('stroke-width', '2');
            path.setAttribute('stroke-dasharray', '8,4');
            path.setAttribute('opacity', '0.8');
          } else {
            path.setAttribute('stroke', '#9e9e9e');
            path.setAttribute('stroke-width', '1.5');
            path.setAttribute('stroke-dasharray', '4,2');
            path.setAttribute('opacity', '0.6');
          }

          let classNames = `mapping-line ${mapping.isAutomatic ? 'automatic' : 'manual'} ${isRightToRight ? 'right-to-right' : 'left-to-right'}`;
          if (isNearlyHorizontal && !isRightToRight) {
            classNames += ' straight-line';
          }
          if (!isLinkMode) {
            classNames += ' read-only';
          }
          if (hasNodeAboveTabs) {
            classNames += ' extends-above-tabs';
          } else if (hasNodeBelowViewport) {
            classNames += ' extends-below-viewport';
          } else if (hasNodeOutsideViewport) {
            classNames += ' extends-outside-viewport';
          }
          path.setAttribute('class', classNames);
          path.style.pointerEvents = 'none';

          // Add title attribute for tooltip when hovering over lines that extend outside viewport
          if (hasNodeAboveTabs) {
            const sourceTabName = ['Header', 'Body', 'Trailer'][mapping.sourceTab] || 'Unknown';
            const targetTabName = ['Header', 'Body', 'Trailer'][mapping.targetTab] || 'Unknown';
            path.setAttribute('title', `Node above tabs area: ${sourceTabName} → ${targetTabName}`);
          } else if (hasNodeBelowViewport) {
            const sourceTabName = ['Header', 'Body', 'Trailer'][mapping.sourceTab] || 'Unknown';
            const targetTabName = ['Header', 'Body', 'Trailer'][mapping.targetTab] || 'Unknown';
            path.setAttribute('title', `Node below viewport: ${sourceTabName} → ${targetTabName}`);
          } else if (hasNodeOutsideViewport) {
            const sourceTabName = ['Header', 'Body', 'Trailer'][mapping.sourceTab] || 'Unknown';
            const targetTabName = ['Header', 'Body', 'Trailer'][mapping.targetTab] || 'Unknown';
            path.setAttribute('title', `Cross-tab mapping: ${sourceTabName} → ${targetTabName}`);
          }

          // Add shadow effect
          const shadow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          shadow.setAttribute('d', d);
          shadow.setAttribute('fill', 'none');
          shadow.setAttribute('stroke', 'rgba(0,0,0,0.1)');
          shadow.setAttribute('stroke-width', '4');
          shadow.setAttribute('style', 'filter: blur(2px)');

          g.appendChild(shadow);
          g.appendChild(path);

          fragment.appendChild(g);
        }
      }

      processedCount = endIndex;

      // Continue processing in batches if there are more mappings
      if (processedCount < visibleMappings.length) {
        requestAnimationFrame(processMappingBatch);
      } else {
        // All mappings processed, append fragment to SVG
        svg.appendChild(fragment);
      }
    };

    // Start processing mappings in batches
    if (visibleMappings.length > 0) {
      processMappingBatch();
    }

    // Draw dragging line
    if (dragging) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

      // Clamp dragging coordinates to viewport (only for the dragging line)
      const clampedStart = clampToViewport(dragging.startX, dragging.startY, viewportWidth, viewportHeight, tabHeight, true);
      
      // Use mousePos if it has moved, otherwise use the start position to prevent random direction
      let endX = mouseHasMoved ? mousePos.x : dragging.startX;
      const endY = mouseHasMoved ? mousePos.y : dragging.startY;
      
      // Ensure the line always extends to the right - never to the left
      if (endX < dragging.startX) {
        endX = dragging.startX + 50; // Minimum distance to the right
      }
      
      const clampedMouse = clampToViewport(endX, endY, viewportWidth, viewportHeight, tabHeight, true);

      const d = createBezierPath(clampedStart.x, clampedStart.y, clampedMouse.x, clampedMouse.y);

      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', '#4caf50');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('stroke-dasharray', '5,5');
      path.setAttribute('class', 'mapping-line dragging');

      svg.appendChild(path);
    }
  }, [visibleMappings, currentTab, dragging, mousePos, mouseHasMoved, dispatch, createBezierPath, clampToViewport, isLinkMode, treeChangeCounter, hiddenMappingLines, currentTabSourceNodes, currentTabTargetNodes]);

  useEffect(() => {
    // Only enable drag operations when both mapping mode and link mode are active
    if (!isMappingMode || !isLinkMode) return;

    const tabMapping = ['Header', 'Body', 'Trailer'];
    const tabName = tabMapping[currentTab] || 'Body';
    const sourceContainer = document.getElementById(`mappingTree${tabName}`);
    const rightContainer = document.getElementById('rightTreeOutbound');

    const handleSourceMouseDown = (e) => {
      const node = e.target.closest('.rc-tree-treenode');
      if (!node || e.target.closest('.rc-tree-switcher')) return;

      const dataKeyElement = node.querySelector('[data-key]');
      if (!dataKeyElement) return;

      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling
      const key = dataKeyElement.getAttribute('data-key');

      // Find the source node data and set it as selected
      const tabMapping = ['header', 'body', 'trailer'];
      const currentTabName = tabMapping[currentTab];
      const sourceTreeData = Array.isArray(inboundDependencyTree?.[currentTabName])
        ? inboundDependencyTree[currentTabName]
        : [];

      const sourceNodeData = findNodeByKey(sourceTreeData, key);
      if (sourceNodeData) {
        // Set the source node as selected immediately for visual feedback
        dispatch(treeActions.setMappingSelectedSourceNode({
          tab: currentTabName,
          selectedNode: sourceNodeData
        }));
      }

      // Get the title text element to find where the text ends
      const titleElement = node.querySelector('.rc-tree-title') || node;
      const titleSpan = titleElement.querySelector('span[data-key]') || titleElement;
      const titleRect = titleSpan.getBoundingClientRect();

      // Get node position for Y coordinate
      const nodeRect = node.getBoundingClientRect();

      // Start from the right edge of the title text
      const startX = titleRect.right + 5; // Small offset after text
      const startY = nodeRect.top + nodeRect.height / 2;

      setDragging({
        sourceKey: key,
        sourceTab: currentTab,
        startX: startX,
        startY: startY,
        isFromRightTree: false
      });

      // Initialize mousePos to the start position to prevent random initial direction
      setMousePos({ x: startX, y: startY });
      setMouseHasMoved(false);

      // Initialize the dragging line immediately with the start position
      // This prevents the line from appearing in random directions
      setTimeout(() => {
        drawDraggingLine(startX, startY);
      }, 0);
    };

    const handleRightTreeMouseDown = (e) => {
      // Disable manual right-to-right mapping creation
      // Right-to-right mappings should only be created automatically
      return;

      const node = e.target.closest('.rc-tree-treenode');
      if (!node || e.target.closest('.rc-tree-switcher')) return;

      const dataKeyElement = node.querySelector('[data-key]');
      if (!dataKeyElement) return;

      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling
      const key = dataKeyElement.getAttribute('data-key');

      // Get the title text element to find where the text ends
      const titleElement = node.querySelector('.rc-tree-title') || node;
      const titleSpan = titleElement.querySelector('span[data-key]') || titleElement;
      const titleRect = titleSpan.getBoundingClientRect();

      // Get node position for Y coordinate
      const nodeRect = node.getBoundingClientRect();

      // Start from the right edge of the title text (for right-to-right connections)
      const startX = titleRect.right + 5; // Small offset after text
      const startY = nodeRect.top + nodeRect.height / 2;

      setDragging({
        sourceKey: key,
        sourceTab: null, // No source tab for right-to-right mappings
        startX: startX,
        startY: startY,
        isFromRightTree: true
      });

      // Initialize mousePos to the start position to prevent random initial direction
      setMousePos({ x: startX, y: startY });
      setMouseHasMoved(false);
    };

    const handleMouseMove = (e) => {
      if (dragging) {
        setMousePos({ x: e.clientX, y: e.clientY });
        setMouseHasMoved(true);
        // Only redraw the dragging line, not all existing lines
        drawDraggingLine(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = (e) => {
      if (!dragging) return;

      const targetNode = e.target.closest('.rc-tree-treenode');
      const dataKeyElement = targetNode?.querySelector('[data-key]');
      const dataKey = dataKeyElement?.getAttribute('data-key');

      // Helper function to get node data for validation
      const getNodeData = (nodeKey, isFromRightTree, sourceTab, targetTabContext = null) => {


        if (isFromRightTree) {
          // For right tree nodes, determine which tab to search in
          const tabMapping = ['header', 'body', 'trailer'];
          let searchTab = targetTabContext !== null ? targetTabContext : currentTab;
          let searchTabName = tabMapping[searchTab];

          let rightTreeData = outboundTree?.rightTree?.tree?.[searchTabName]?.translatedData || [];

          let result = findNodeByKey(rightTreeData, nodeKey);

          // If not found in specified tab, try all tabs for cross-tab support
          if (!result) {

            for (const tab of ['header', 'body', 'trailer']) {
              rightTreeData = outboundTree?.rightTree?.tree?.[tab]?.translatedData || [];

              result = findNodeByKey(rightTreeData, nodeKey);
              if (result) {

                break;
              }
            }
          } else {

          }


          return result;
        } else {
          // For left tree nodes, get from source tab
          const tabMapping = ['header', 'body', 'trailer'];
          const tabName = tabMapping[sourceTab];


          // First try to get from the specified source tab
          let leftTreeData = inboundDependencyTree?.[tabName] || [];

          let nodeData = findNodeByKey(leftTreeData, nodeKey);

          // If not found in source tab, try all tabs for cross-tab support
          if (!nodeData) {

            for (const tab of ['header', 'body', 'trailer']) {
              leftTreeData = inboundDependencyTree?.[tab] || [];

              nodeData = findNodeByKey(leftTreeData, nodeKey);
              if (nodeData) {

                break;
              }
            }
          } else {

          }


          return nodeData;
        }
      };

      // For right-to-right mappings, target must be in right tree and not the source node
      if (dragging.isFromRightTree) {
        // Disable manual right-to-right mapping creation
        // Right-to-right mappings should only be created automatically
        cleanupDragging();
        return;

        // The following code is disabled to prevent manual right-to-right mapping creation
        /*
        if (targetNode && targetNode.closest('#rightTreeOutbound')) {
          const dataKeyElement = targetNode.querySelector('[data-key]');
          if (dataKeyElement) {
            const targetKey = dataKeyElement.getAttribute('data-key');
            
            // Don't allow self-mapping
            if (targetKey === dragging.sourceKey) {
              cleanupDragging();
              return;
            }
            
            // Get source and target node data for validation
            const sourceNodeData = getNodeData(dragging.sourceKey, true, null);
            const targetNodeData = getNodeData(targetKey, true, null);
            
            // Validate the mapping
            const validation = validateMapping(sourceNodeData, targetNodeData);
            if (!validation.isValid) {
              showError(validation.errorMessage);
              cleanupDragging();
              return;
            }
            
            // Verify the target node is actually visible
            const targetRect = targetNode.getBoundingClientRect();
            const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
            
            if (targetRect.top < viewportHeight && targetRect.bottom > 0) {
              // Remove any existing mappings to this target node first
              dispatch(treeActions.removeMappingsByTargetKey({
                targetKey: targetKey,
                sourceTab: null // For right-to-right mappings
              }));
              
              // Add the new mapping
              dispatch(treeActions.addMapping({
                sourceKey: dragging.sourceKey,
                targetKey,
                sourceTab: null,
                mappingType: 'right-to-right'
              }));
            }
          }
        }
        */
      } else {
        // Traditional left-to-right mapping
        if (targetNode && targetNode.closest('#rightTreeOutbound')) {
          const dataKeyElement = targetNode.querySelector('[data-key]');
          if (dataKeyElement) {
            const targetKey = dataKeyElement.getAttribute('data-key');

            // Determine which tab the target node belongs to by checking the DOM context
            let targetTab = currentTab; // default to current tab
            const tabPanels = targetNode.closest('[role="tabpanel"]');
            if (tabPanels) {
              const tabId = tabPanels.getAttribute('id');
              if (tabId?.includes('header')) targetTab = 0;
              else if (tabId?.includes('body')) targetTab = 1;
              else if (tabId?.includes('trailer')) targetTab = 2;
            }


            
            // Get source and target node data for validation
            const sourceNodeData = getNodeData(dragging.sourceKey, false, dragging.sourceTab);
            const targetNodeData = getNodeData(targetKey, true, null, targetTab);


            // Validate the mapping
            const validation = validateMapping(sourceNodeData, targetNodeData, false, 'left-to-right', false, dragging.sourceTab, {header: 0, body: 1, trailer: 2}[outboundTree.rightTree.currentTab]);
            if (!validation.isValid) {    
              console.error('Mapping validation failed:', validation.errorMessage);
              showError(validation.errorMessage);
              cleanupDragging();
              return;
            }

            // Verify the target node is actually visible
            const targetRect = targetNode.getBoundingClientRect();
            const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

            if (targetRect.top < viewportHeight && targetRect.bottom > 0) {
              // Remove any existing mappings to this target node first
              dispatch(treeActions.removeMappingsByTargetKey({
                targetKey: targetKey,
                sourceTab: dragging.sourceTab
              }));

              // Add the new mapping
              dispatch(treeActions.addMapping({
                sourceKey: dragging.sourceKey,
                targetKey,
                sourceTab: dragging.sourceTab,
                mappingType: 'left-to-right'
              }));
              // Also call the saveMappingToRedux function for data persistence
              saveMappingToRedux(dragging.sourceKey, targetKey, dragging.sourceTab);
            }
          }
        }
      }

      cleanupDragging();
    };

    // Add listeners
    if (sourceContainer) {
      sourceContainer.addEventListener('mousedown', handleSourceMouseDown);
    }
    if (rightContainer) {
      rightContainer.addEventListener('mousedown', handleRightTreeMouseDown);
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (sourceContainer) {
        sourceContainer.removeEventListener('mousedown', handleSourceMouseDown);
        sourceContainer.removeEventListener('click', sourceContainer._clickHandler);
      }
      if (rightContainer) {
        rightContainer.removeEventListener('mousedown', handleRightTreeMouseDown);
        rightContainer.removeEventListener('click', rightContainer._clickHandler);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMappingMode, isLinkMode, currentTab, dragging, dispatch, mappings, automaticMappings, showError, outboundTree, inboundDependencyTree, saveMappingToRedux]);

  // Redraw connections when mappings or current tab changes
  useEffect(() => {
    // Clear any pending scroll redraws when mappings change
    if (svgRef.current && svgRef.current._scrollTimeout) {
      clearTimeout(svgRef.current._scrollTimeout);
    }
    redrawConnections();
  }, [visibleMappings, currentTab, redrawConnections]);

  // Special handling for tab changes to ensure proper line positioning
  useEffect(() => {
    // Clear any existing dragging lines when tab changes
    if (svgRef.current) {
      // Clear any pending scroll redraws
      if (svgRef.current._scrollTimeout) {
        clearTimeout(svgRef.current._scrollTimeout);
      }
      
      const draggingLine = svgRef.current.querySelector('#dragging-line');
      if (draggingLine) {
        draggingLine.remove();
      }

      // Clear all existing mapping lines to prevent showing previous tab's lines
      const existingLines = svgRef.current.querySelectorAll('path:not(#dragging-line)');
      existingLines.forEach(line => line.remove());
    }

    // Force a redraw after tab change to ensure proper positioning
    const timeoutId = setTimeout(() => {
      redrawConnections();
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(timeoutId);
  }, [currentTab, redrawConnections]);

  // Simplified event handlers without excessive memoization
  const handleResize = useCallback(() => {
    redrawConnections();
  }, [redrawConnections]);

  const handleScroll = useCallback(() => {
    // Add a small delay to prevent excessive redraws during rapid scrolling
    if (svgRef.current) {
      // Clear any existing timeout
      if (svgRef.current._scrollTimeout) {
        clearTimeout(svgRef.current._scrollTimeout);
      }
      
      // Set a new timeout for redrawing
      svgRef.current._scrollTimeout = setTimeout(() => {
        redrawConnections();
      }, 10); // Small delay to batch rapid scroll events
    }
  }, [redrawConnections]);

  // Handle window resize and scroll events
  useEffect(() => {
    // Listen to window resize
    window.addEventListener('resize', handleResize);

    // Find and listen to the actual scrollable containers
    const scrollContainers = [];

    // Left side tree containers (these are the actual scrollable divs)
    const mappingTreeHeader = document.getElementById('mappingTreeHeader');
    const mappingTreeBody = document.getElementById('mappingTreeBody');
    const mappingTreeTrailer = document.getElementById('mappingTreeTrailer');

    if (mappingTreeHeader) scrollContainers.push(mappingTreeHeader);
    if (mappingTreeBody) scrollContainers.push(mappingTreeBody);
    if (mappingTreeTrailer) scrollContainers.push(mappingTreeTrailer);

    // Right side tree containers (find the scrollable divs inside rightTreeOutbound)
    const rightTreeOutbound = document.getElementById('rightTreeOutbound');
    if (rightTreeOutbound) {
      const rightScrollDivs = rightTreeOutbound.querySelectorAll('.overflow-auto');
      rightScrollDivs.forEach(div => scrollContainers.push(div));
    }

    // Add scroll listeners to all containers with throttling
    scrollContainers.forEach(container => {
      const throttledScrollHandler = throttle(handleScroll, 16); // ~60fps
      container.addEventListener('scroll', throttledScrollHandler, { passive: true });
      // Store the throttled handler for cleanup
      container._throttledScrollHandler = throttledScrollHandler;
    });

    // Also check for any parent scrollable containers
    const mappingTreeContainer = document.getElementById('mappingTreeContainer');
    if (mappingTreeContainer) {
      const throttledScrollHandler = throttle(handleScroll, 16); // ~60fps
      mappingTreeContainer.addEventListener('scroll', throttledScrollHandler, { passive: true });
      mappingTreeContainer._throttledScrollHandler = throttledScrollHandler;
    }

    // Redraw on mount and resize
    const resizeObserver = new ResizeObserver(handleResize);
    const splitPanes = document.querySelectorAll('.split');

    splitPanes.forEach(pane => {
      if (pane) {
        resizeObserver.observe(pane);
      }
    });

    // Also observe for gutter drag changes
    const mutationObserver = new MutationObserver(() => {
      redrawConnections();
    });

    const gutters = document.querySelectorAll('.gutter');
    gutters.forEach(gutter => {
      if (gutter) {
        mutationObserver.observe(gutter, { attributes: true, attributeFilter: ['style'] });
      }
    });

    // Initial draw with delay to ensure DOM is fully rendered
    const initialDrawTimeout = setTimeout(() => {
      redrawConnections();
    }, 50);

    // Force a redraw after mount to ensure proper positioning
    const finalDrawTimeout = setTimeout(() => {
      redrawConnections();
    }, 150);

        return () => {
      clearTimeout(initialDrawTimeout);
      clearTimeout(finalDrawTimeout);
      // Clear any pending scroll redraws
      if (svgRef.current && svgRef.current._scrollTimeout) {
        clearTimeout(svgRef.current._scrollTimeout);
      }
      
      window.removeEventListener('resize', handleResize);
      scrollContainers.forEach(container => {
        if (container && container._throttledScrollHandler) {
          container.removeEventListener('scroll', container._throttledScrollHandler);
          delete container._throttledScrollHandler;
        }
      });
      if (mappingTreeContainer && mappingTreeContainer._throttledScrollHandler) {
        mappingTreeContainer.removeEventListener('scroll', mappingTreeContainer._throttledScrollHandler);
        delete mappingTreeContainer._throttledScrollHandler;
      }
      splitPanes.forEach(pane => {
        if (pane) {
          resizeObserver.unobserve(pane);
        }
      });
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [redrawConnections, handleResize, handleScroll]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts when component unmounts
      if (svgRef.current && svgRef.current._scrollTimeout) {
        clearTimeout(svgRef.current._scrollTimeout);
        delete svgRef.current._scrollTimeout;
      }
    };
  }, []);

  if (!isMappingMode) return null;

  return (
    <>
      <svg
        ref={svgRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 50
        }}
      />
      
      {/* Loading Overlay for Auto-Mapping - Tree Areas Only */}
      {isAutoMappingLoading && (
        <>
          {/* Left Tree Overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '50vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '15px 20px',
                borderRadius: '6px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #2196f3',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                Analyzing Source Tree...
              </div>
            </div>
          </div>

          {/* Right Tree Overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: '50vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '15px 20px',
                borderRadius: '6px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #2196f3',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                Analyzing Target Tree...
              </div>
            </div>
          </div>
        </>
      )}
      
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={hideToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      
      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default SimpleMappingConnections;